import { useRef, useState } from "react";

const MAX_PHOTOS = 8;
const MAX_DIM = 1200; // px — the full image shown on a listing page
// The browse grid renders ~200px tiles. Serving the 1200px original into them
// was the single biggest cost of loading the grid, so each upload also stores a
// small copy and the grid uses that instead.
const THUMB_DIM = 400;

function drawToDataUrl(img, maxDim, quality) {
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

/* Produces BOTH sizes from one decode. They travel together as { full, thumb }
   for the rest of their life — keeping two parallel arrays in sync through
   reordering and removal is exactly the kind of thing that silently drifts. */
function fileToPhoto(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not load image"));
      img.onload = () => {
        resolve({
          full: drawToDataUrl(img, MAX_DIM, 0.82),
          thumb: drawToDataUrl(img, THUMB_DIM, 0.7),
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Controlled: `value` is an array of data-URL strings; `onChange(next)`.
export function PhotoUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const room = MAX_PHOTOS - value.length;
    if (room <= 0) return;
    setBusy(true);
    try {
      const added = [];
      for (const file of files.slice(0, room)) {
        try {
          added.push(await fileToPhoto(file));
        } catch {
          /* skip unreadable file */
        }
      }
      onChange([...value, ...added]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeAt(i) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((item, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
            <img
              src={item.thumb || item.full}
              alt={`Upload ${i + 1}`}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label="Remove photo"
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white text-sm leading-none flex items-center justify-center hover:bg-black/80"
            >
              ×
            </button>
            {i === 0 && (
              <span className="absolute bottom-1 left-1 bg-brand-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                Cover
              </span>
            )}
          </div>
        ))}

        {value.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-brand-400 hover:text-brand-500 flex flex-col items-center justify-center gap-1 disabled:opacity-50"
          >
            <span className="text-2xl leading-none">{busy ? "…" : "＋"}</span>
            <span className="text-xs">{busy ? "Processing" : "Add photo"}</span>
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Up to {MAX_PHOTOS} photos. First photo is the cover. {value.length}/{MAX_PHOTOS} added.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
